import { useMemo, useState } from 'react'

import { useQueries, useQuery } from '@tanstack/react-query'
import { CellValueChangedEvent, RowClickedEvent } from 'ag-grid-enterprise'
import { Check, ChevronDownIcon, X } from 'lucide-react'
import { renderToString } from 'react-dom/server'
import { useDebounceValue } from 'usehooks-ts'

import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createColDefs, createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Spinner } from '@fortem-monorepo/react-ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@fortem-monorepo/react-ui'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@fortem-monorepo/react-ui'
import { Audit, Instruments } from '@fortem-monorepo/web-sdk'

import { audit } from '@/ag-grid/columns/audit/col-def'
import { Page } from '@/components/Page'
import { INSTRUMENTS } from '@/constants/links'
import { useApproveElementMutation } from '@/react-query/mutations/audit'
import {
  useUpdateBasketoptionMutation,
  useUpdateInstrumentMutation,
  useUpdateMarketdataInstrumentMutation,
} from '@/react-query/mutations/instruments'
import { useInternalUser } from '@/react-query/queries/account'

const inputTypeOptions = [
  { label: 'Structure Name', value: 'structurename' },
  { label: 'Description', value: 'description' },
  { label: 'Isin', value: 'isin' },
  { label: 'Asset Identifier', value: 'assetidentifier' },
  { label: 'Comment', value: 'comment' },
  { label: 'Fund', value: 'fundname' },
  { label: 'BBG Ticker', value: 'bbgticker' },
  { label: 'Ticker', value: 'ticker' },
  { label: 'Aset Class', value: 'assetclass' },
  { label: 'Factor', value: 'factor' },
]

type TabType = 'instruments' | 'basketoptions' | 'marketdata-instruments'

export interface InstrumentsPageProps {}

export const InstrumentsPage: React.FC<InstrumentsPageProps> = ({}) => {
  const [tab, setTab] = useState<TabType>('instruments')
  const [selectedStructurename, setSelectedStructurename] = useState<string>()
  const [inputType, setInputType] = useState<string>()
  const [inputValue, setInputValue] = useState<string>()

  const api = useApi()

  const { internalUser } = useInternalUser()

  const [debouncedValue] = useDebounceValue(inputValue, 600)

  const { hasPermissions } = useUserContext()

  const canEdit = hasPermissions((permissions) => ({
    permissions: [permissions.CREATE_INSTRUMENTS],
  }))

  const olddbParams = useMemo(() => {
    if (!inputType || !debouncedValue) return {}
    return {
      input_type: inputType,
      value: debouncedValue,
    }
  }, [debouncedValue, inputType])

  const secmInstrumentsQuery = useQuery({
    queryKey: ['instruments'],
    queryFn: () => api.instruments.listAll(),
    enabled: tab === 'instruments',
  })

  const instrumentsData = useMemo(() => {
    return secmInstrumentsQuery.data ?? []
  }, [secmInstrumentsQuery])

  // ------------

  const basketoptionsQuery = useQuery({
    queryKey: ['basketoptions', olddbParams],
    queryFn: () => api.instruments.listAllBasketoptions(olddbParams),
    enabled: tab === 'basketoptions',
  })

  const basketoptionsData = useMemo(() => {
    return basketoptionsQuery.data ?? []
  }, [secmInstrumentsQuery])

  // ------------

  const marketdataQuery = useQuery({
    queryKey: ['marketdata-instruments', olddbParams],
    queryFn: () => api.instruments.listAllMarketdataInstruments(olddbParams),
    enabled: tab === 'marketdata-instruments',
  })

  const marketdataInstrumentsData = useMemo(() => {
    return marketdataQuery.data ?? []
  }, [marketdataQuery])

  // ------------

  const olddbInspectionQuery = useQueries({
    queries: [
      {
        queryKey: ['vanillaoptions', selectedStructurename],
        queryFn: () =>
          api.instruments.listAllVanillaoptions({ structurename: selectedStructurename! }),
        enabled: !!selectedStructurename,
      },
      {
        queryKey: ['barrieroptions', selectedStructurename],
        queryFn: () =>
          api.instruments.listAllBarrieroptions({ structurename: selectedStructurename! }),
        enabled: !!selectedStructurename,
      },
    ],
  })

  const approveElementMutation = useApproveElementMutation()

  const [vanillaoptionsQuery, barrieroptionsQuery] = olddbInspectionQuery

  const vanillaoptionData = useMemo(() => {
    return vanillaoptionsQuery.data ?? []
  }, [vanillaoptionsQuery])

  const barrieroptionData = useMemo(() => {
    return barrieroptionsQuery.data ?? []
  }, [barrieroptionsQuery])

  const updateInstrumentMutation = useUpdateInstrumentMutation()
  const updateBasketoptionMutation = useUpdateBasketoptionMutation()
  const updateMarketdataInstrumentMutation = useUpdateMarketdataInstrumentMutation()

  const instrumentsContextMenu = createContextMenu<Instruments.ViewInstrument>((params) => {
    const status = params.node?.data?.status
    const element_id = params.node?.data?.instrument_id

    const statusIsNotApproved = status !== Audit.STATUSES.APPROVED
    const lastUpdatedByUser = internalUser?.user_id == params.node?.data?.submitted_by

    const canApprove = statusIsNotApproved && !lastUpdatedByUser

    return [
      ...(canApprove
        ? ([
            {
              name: 'Approve Instrument',
              icon: renderToString(<Check size={16} />),
              cssClasses: ['hover:bg-green-100!', 'text-green-600!'],
              action: () => {
                return approveElementMutation.mutateAsync({
                  element_id,
                  element_type: Audit.ELEMENT_TYPES.INSTRUMENT,
                })
              },
            },
            'separator',
          ] satisfies CustomMenuItem[])
        : []),
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'separator',
      'export',
    ]
  })

  const basketoptionsContextMenu = createContextMenu<Instruments.Basketoption>((params) => {
    const status = params.node?.data?.status
    const element_id = params.node?.data?.structurename!

    const statusIsNotApproved = status !== Audit.STATUSES.APPROVED
    const lastUpdatedByUser = internalUser?.user_id == params.node?.data?.submitted_by

    const canApprove = statusIsNotApproved && !lastUpdatedByUser

    return [
      ...(canApprove
        ? ([
            {
              name: 'Approve Instrument',
              icon: renderToString(<Check size={16} />),
              cssClasses: ['hover:bg-green-100!', 'text-green-600!'],
              action: () => {
                return approveElementMutation.mutateAsync({
                  element_id,
                  element_type: Audit.ELEMENT_TYPES.OLD_INSTRUMENT,
                })
              },
            },
            'separator',
          ] satisfies CustomMenuItem[])
        : []),
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'separator',
      'export',
    ]
  })

  return (
    <Page metadata={{ title: 'Booked Instruments' }} permissions={INSTRUMENTS.ALL.permissions}>
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabType)} className={'gap-0 flex flex-1'}>
        <HeaderConfigBar>
          <div className="flex items-center gap-2">
            <TabsList>
              <TabsTrigger value="instruments">secm.instrument</TabsTrigger>
              <TabsTrigger value="basketoptions">prodstatics.basketoption</TabsTrigger>
              <TabsTrigger value="marketdata-instruments">marketdata.instrument</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              {(tab === 'basketoptions' || tab === 'marketdata-instruments') && (
                <InputGroupDropdown
                  placeholder={'Filter'}
                  value={inputValue}
                  onChange={(v) => setInputValue(v)}
                  dropdownOptions={inputTypeOptions}
                  dropdownValue={inputType}
                  onSelectDropdown={(v) => setInputType(v)}
                  onClearClick={() => {
                    setInputType(undefined)
                    setInputValue('')
                  }}
                />
              )}
            </div>
          </div>
        </HeaderConfigBar>
        <TabsContent value="instruments">
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery query={secmInstrumentsQuery} title="secm.instrument" />
            <WidgetContent loading={secmInstrumentsQuery.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                rowData={instrumentsData}
                columnDefs={createInstrumentColDefs(canEdit)}
                getContextMenuItems={instrumentsContextMenu}
                onCellValueChanged={(params: CellValueChangedEvent<Instruments.Instrument>) => {
                  return updateInstrumentMutation.mutateAsync({
                    rowData: params.data,
                    oldValue: params.oldValue,
                    newValue: params.newValue,
                    updatedField: params.colDef.field!,
                  })
                }}
              />
            </WidgetContent>
          </Widget>
        </TabsContent>
        <TabsContent value="basketoptions" className={'flex flex-col flex-1'}>
          <ResizablePanelGroup direction={'vertical'}>
            <ResizablePanel>
              <Widget exposed className={'flex h-full'}>
                <WidgetHeaderWithQuery
                  query={basketoptionsQuery}
                  title="prodstatics.basketoption"
                />
                <WidgetContent loading={basketoptionsQuery.isLoading}>
                  <CustomAgGrid
                    onRowClicked={(params: RowClickedEvent<{ structurename: string }>) => {
                      setSelectedStructurename(params.data?.structurename)
                    }}
                    domLayout={'normal'}
                    rowData={basketoptionsData}
                    getContextMenuItems={basketoptionsContextMenu}
                    columnDefs={createBasketoptionColDefs(canEdit)}
                    onCellValueChanged={(params: CellValueChangedEvent<Instruments.Instrument>) => {
                      return updateBasketoptionMutation.mutateAsync({
                        rowData: params.data,
                        oldValue: params.oldValue,
                        newValue: params.newValue,
                        updatedField: params.colDef.field!,
                      })
                    }}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            {selectedStructurename && (
              <>
                <ResizableHandle />
                <ResizablePanel>
                  <Tabs defaultValue={'vanillaoptions'} className={'gap-0 h-full'}>
                    <HeaderConfigBar>
                      <div className="flex items-center gap-2">
                        <TabsList>
                          <TabsTrigger value="vanillaoptions">
                            <span>Vanillaoptions</span>
                            {vanillaoptionsQuery.isFetching ? (
                              <Spinner />
                            ) : (
                              vanillaoptionData.length
                            )}
                          </TabsTrigger>
                          <TabsTrigger value="barrieroptions">
                            <span>Barrieroptions</span>
                            {barrieroptionsQuery.isFetching ? (
                              <Spinner />
                            ) : (
                              barrieroptionData.length
                            )}
                          </TabsTrigger>
                        </TabsList>
                      </div>
                    </HeaderConfigBar>
                    <TabsContent value="vanillaoptions">
                      <Widget exposed className={'flex h-full'}>
                        <WidgetHeaderWithQuery
                          query={vanillaoptionsQuery}
                          title={`prodstatics.vanillaoption - ${selectedStructurename}`}
                        />
                        <WidgetContent loading={vanillaoptionsQuery.isLoading}>
                          <CustomAgGrid
                            domLayout={'normal'}
                            rowData={vanillaoptionData}
                            columnDefs={createVanillaoptionColDefs(canEdit)}
                          />
                        </WidgetContent>
                      </Widget>
                    </TabsContent>
                    <TabsContent value="barrieroptions">
                      <Widget exposed className={'flex h-full'}>
                        <WidgetHeaderWithQuery
                          query={barrieroptionsQuery}
                          title={`prodstatics.barrieroption - ${selectedStructurename}`}
                        />
                        <WidgetContent loading={barrieroptionsQuery.isLoading}>
                          <CustomAgGrid
                            domLayout={'normal'}
                            rowData={barrieroptionData}
                            columnDefs={createBarrieroptionColDefs(canEdit)}
                          />
                        </WidgetContent>
                      </Widget>
                    </TabsContent>
                  </Tabs>
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        </TabsContent>
        <TabsContent value="marketdata-instruments">
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery query={marketdataQuery} title="marketdata.instrument" />
            <WidgetContent loading={marketdataQuery.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                rowData={marketdataInstrumentsData}
                columnDefs={createMarketdataColDefs(canEdit)}
                onCellValueChanged={(params: CellValueChangedEvent<Instruments.Instrument>) => {
                  return updateMarketdataInstrumentMutation.mutateAsync({
                    rowData: params.data,
                    oldValue: params.oldValue,
                    newValue: params.newValue,
                    updatedField: params.colDef.field!,
                  })
                }}
              />
            </WidgetContent>
          </Widget>
        </TabsContent>
      </Tabs>
    </Page>
  )
}

export interface InputGroupDropdownProps {
  placeholder?: string
  value?: string
  dropdownOptions?: { label: string; value: string }[]
  dropdownValue?: string
  onChange?: (v: string) => void
  onSelectDropdown?: (value: string) => void
  onClearClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const InputGroupDropdown: React.FC<InputGroupDropdownProps> = ({
  onClearClick,
  value,
  dropdownOptions,
  dropdownValue,
  onChange,
  onSelectDropdown,
  placeholder,
}) => {
  return (
    <div className="grid w-full max-w-sm gap-4">
      <InputGroup size={'sm'}>
        <InputGroupInput
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange?.(e.target.value)
          }}
        />
        <InputGroupAddon align="inline-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <InputGroupButton variant="ghost" className="pr-1.5! text-xs">
                {dropdownValue ?? 'Select filter'} <ChevronDownIcon className="size-3" />
              </InputGroupButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                {dropdownOptions?.map((o) => (
                  <DropdownMenuItem onClick={() => onSelectDropdown?.(o.value)}>
                    {o.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </InputGroupAddon>
        {onClearClick && (
          <InputGroupAddon align={'inline-end'}>
            <InputGroupButton
              variant={'ghost'}
              onClick={(e) => {
                onClearClick(e)
              }}
            >
              <X />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
    </div>
  )
}

const createInstrumentColDefs = (canEdit: boolean) => {
  return createColDefs<Instruments.ViewInstrument>([
    audit,
    { field: 'instrument_id', headerName: 'ID', width: 100, pinned: true },
    { field: 'unique_identifier', pinned: true },
    { field: 'isin' },
    { field: 'sedol', editable: canEdit },
    { field: 'figi', editable: canEdit },
    { field: 'cusip', editable: canEdit },
    { field: 'bbg_ticker', editable: canEdit },
    { field: 'format' },
    { field: 'price_type', editable: canEdit },
    { field: 'price_source' },
    { field: 'issuer', editable: canEdit },
    { field: 'denomination' },
    { field: 'asset_class', editable: canEdit },
    { field: 'factor', editable: canEdit },
    { field: 'sub_factor', editable: canEdit },
    { field: 'short_name', editable: canEdit },
    { field: 'long_name', editable: canEdit },
    { field: 'marketing' },
    { field: 'is_component' },
    { field: 'iso3_country', editable: canEdit },
    { field: 'currency_id' },
    { field: 'currency' },
    { field: 'instrument_type_id' },
    { field: 'instrument_type' },
    { field: 'id' },
    { field: 'effective_date', editable: canEdit },
    { field: 'first_payment_date', editable: canEdit },
    { field: 'penultimate_payment_date', editable: canEdit },
    { field: 'cash_settlement_date', editable: canEdit },
    { field: 'maturity_date', editable: canEdit },
    { field: 'expiry_date', editable: canEdit },
    { field: 'quote_factor', headerName: 'Quote Factor' },
    { field: 'unit_multiplier', headerName: 'Unit Multiplier' },
  ])
}

const createBasketoptionColDefs = (canEdit: boolean) => {
  return createColDefs<any>([
    audit,
    { field: 'id', pinned: true },
    { field: 'structurename', pinned: true },
    { field: 'ccy', editable: canEdit },
    { field: 'isin' },
    { field: 'description', editable: canEdit },
    { field: 'sedol', type: 'text', editable: canEdit },
    { field: 'initobsdate' },
    { field: 'notional' },
    { field: 'tradeid', type: 'text' },
    { field: 'tradedate' },
    { field: 'maturitydate' },
    { field: 'assetidentifier' },
    { field: 'addreference' },
    { field: 'floaterspread' },
    { field: 'firstfloatersettlementdate' },
    { field: 'secondlastfloatersettlementdate' },
    { field: 'effectivedate' },
    { field: 'cashsettlementpaymentdate' },
    { field: 'fundingtype', editable: canEdit },
    { field: 'isautocallable', type: 'text' },
    { field: 'bankcoropticker' },
    { field: 'cptyid', editable: canEdit },
    { field: 'floaterinstrumentticker', editable: canEdit },
    { field: 'isfloaterfunding' },
    { field: 'isnotefunding' },
    { field: 'isfund' },
    { field: 'issecurity' },
    { field: 'comment', editable: canEdit },
    { field: 'incsecondarywebpage' },
    { field: 'bloombergcode', editable: canEdit },
    { field: 'fundname', editable: canEdit },
    { field: 'pricetype', editable: canEdit },
    { field: 'resetfreq', editable: canEdit },
    { field: 'iscalibrationprod' },
    { field: 'fixeefundingfee' },
    { field: 'isfixedfunding' },
    { field: 'isdispersiontrade' },
    { field: 'isvolvariancetrade' },
    { field: 'positionleverage', editable: canEdit },
    { field: 'positionleveragetype', editable: canEdit },
    { field: 'positionleverageupdate' },
    { field: 'decomposefund' },
    { field: 'decomposefundname' },
    { field: 'fundporttype', editable: canEdit },
    { field: 'issuer' },
    { field: 'bucket', editable: canEdit },
  ])
}

const createVanillaoptionColDefs = (_canEdit: boolean) => {
  return createColDefs<any>([
    { field: 'id' },
    { field: 'participationrate' },
    { field: 'strike' },
    { field: 'strikepercent' },
    { field: 'paydate' },
    { field: 'startobservationdate' },
    { field: 'endobservationdate' },
    { field: 'maturitydate' },
    { field: 'structurename' },
    { field: 'iscall' },
    { field: 'isput' },
    { field: 'iseuropean' },
    { field: 'isamerican' },
    { field: 'isasian' },
    { field: 'isshort' },
    { field: 'islong' },
    { field: 'initobslvl' },
    { field: 'initobsdate' },
    { field: 'instrumentticker' },
    { field: 'optionclusterid' },
    { field: 'basketweight' },
    { field: 'basketgrouping' },
  ])
}

const createBarrieroptionColDefs = (_canEdit: boolean) => {
  return createColDefs<any>([
    { field: 'id' },
    { field: 'couponrate' },
    { field: 'payoffbarrierlvlpercent' },
    { field: 'autocallbarrierlvlpercent' },
    { field: 'instrumentticker' },
    { field: 'structurename' },
    { field: 'barrierdate' },
    { field: 'initobslvl' },
    { field: 'triggerbarrierlvlpercent' },
    { field: 'paydate' },
    { field: 'participationrate' },
    { field: 'endobservationdate' },
    { field: 'startobservationdate' },
    { field: 'vanillaoptionid' },
    { field: 'isupandout' },
    { field: 'isdownandout' },
    { field: 'isupandin' },
    { field: 'isdownandin' },
    { field: 'iseuropean' },
    { field: 'isamerican' },
    { field: 'isasian' },
    { field: 'isdigital' },
    { field: 'isvanillaoption' },
    { field: 'initobsdate' },
    { field: 'islong' },
    { field: 'isshort' },
    { field: 'optionclusterid' },
    { field: 'basketweight' },
    { field: 'basketgrouping' },
  ])
}

const createMarketdataColDefs = (canEdit: boolean) => {
  return createColDefs<Instruments.MarketdataInstrument>([
    { field: 'id' },
    { field: 'leveragefactor', editable: canEdit },
    { field: 'isin', editable: canEdit },
    { field: 'bbgticker' },
    { field: 'ccy', editable: canEdit },
    { field: 'name', editable: canEdit },
    { field: 'ticker' },
    { field: 'tenormonths', editable: canEdit },
    { field: 'tickerindefaultccy', editable: canEdit },
    { field: 'ccy2', editable: canEdit },
    { field: 'curveid', editable: canEdit },
    { field: 'ratestype', editable: canEdit },
    { field: 'assetclass', editable: canEdit },
    { field: 'factor', editable: canEdit },
    { field: 'subfactor', editable: canEdit },
    { field: 'issuercode', editable: canEdit },
    { field: 'isindex', editable: canEdit },
    { field: 'isinrfq', editable: canEdit },
    { field: 'isindefaultccy', editable: canEdit },
    { field: 'iscommodity', editable: canEdit },
    { field: 'isfx', editable: canEdit },
    { field: 'isbond', editable: canEdit },
    { field: 'isinflation', editable: canEdit },
    { field: 'axeflag', editable: canEdit },
    { field: 'region', editable: canEdit },
    { field: 'isrates', editable: canEdit },
    { field: 'isequity', editable: canEdit },
    { field: 'iscds', editable: canEdit },
  ])
}
